import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, LogOut, ChevronDown, ChevronUp, ArrowLeft, TrendingUp, Settings, Users as UsersIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ProfileCompletionBar from '../onboarding/ProfileCompletionBar';
import { Wallet } from 'lucide-react';
import { ALL_NAV_GROUPS, BOTTOM_LINKS, PARENT_NAV_GROUPS, PARENT_BOTTOM_LINKS } from './sidebar/navConfig';
import useAdultMode from '../../hooks/useAdultMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [schoolBranding, setSchoolBranding] = useState(null);
  const isPreviewMode = !!sessionStorage.getItem('hibff_preview_mode');

  // School students and corporate team members don't see Family/Parent sections
  // Moved before exitPreview to avoid "Cannot access before initialization" error
  const isSchoolStudent = !!user?.school_id;
  const isCorporateMember = !!(user?.company_id || user?.org_id);
  const isOrgUser = isSchoolStudent || isCorporateMember;

  // Sprint 208 Master item #5 — Parent role gets a LEAN nav: dashboard only
  // in the top group + 5 fixed bottom links. Teens (default) keep the full tree.
  const isParent = user?.role === 'parent';

  // Phase C/D — adult-mode remaps the teen-flavoured labels and filters
  // children flagged adultOnly / teenOnly inside any group.
  //   • "Family" → "Network"
  //   • "Hearts Store" → "Pro Packs" (Wallet icon)
  //   • Apps menu surfaces Career Playbook / Negotiation Simulator /
  //     Tone Analytics / Boundary Lab for adults; hides Real Connections,
  //     Challenges, Meetups, Leaderboard, Celebrations, Invite a Friend,
  //     Social Quests for adults (teen-flagged content).
  const isAdultMode = useAdultMode();
  const filterChildren = (group) => {
    if (!Array.isArray(group.children)) return group;
    const filtered = group.children.filter(c =>
      isAdultMode ? !c.teenOnly : !c.adultOnly
    );
    return { ...group, children: filtered };
  };
  const applyAdultLabels = (groups, links) => {
    const remappedGroups = groups
      .map(g => isAdultMode && g.id === 'family' ? { ...g, label: 'Network' } : g)
      .map(filterChildren);
    if (!isAdultMode) return { groups: remappedGroups, links };
    const remappedLinks = links.map(l =>
      l.label === 'Hearts Store' ? { ...l, label: 'Pro Packs', icon: Wallet } : l
    );
    return { groups: remappedGroups, links: remappedLinks };
  };

  // Filter out Family section for school/corporate users.
  // Computed BEFORE the auto-expand effect below so the dependency array
  // can reference it without a hoisting hack.
  const baseNavGroups = isParent
    ? PARENT_NAV_GROUPS
    : (isOrgUser ? ALL_NAV_GROUPS.filter(g => g.id !== 'family') : ALL_NAV_GROUPS);

  const baseBottomLinks = isParent ? [...PARENT_BOTTOM_LINKS] : [...BOTTOM_LINKS];
  if (user?.role === 'admin') {
    baseBottomLinks.push({ icon: TrendingUp, label: 'Admin', path: '/admin' });
  }

  const { groups: navGroups, links: bottomLinks } = applyAdultLabels(baseNavGroups, baseBottomLinks);

  const exitPreview = useCallback(async () => {
    const adminToken = sessionStorage.getItem('hibff_admin_token_backup');
    if (adminToken) {
      const wasLocalStorage = sessionStorage.getItem('hibff_admin_storage_type') === 'local';
      if (wasLocalStorage) {
        localStorage.setItem('hibff_auth_token', adminToken);
        sessionStorage.removeItem('hibff_auth_token');
      } else {
        sessionStorage.setItem('hibff_auth_token', adminToken);
        localStorage.removeItem('hibff_auth_token');
      }
      sessionStorage.removeItem('hibff_admin_token_backup');
      sessionStorage.removeItem('hibff_admin_storage_type');
      sessionStorage.removeItem('hibff_preview_mode');
      try { await refreshUser(); } catch {}
    }
    navigate(isCorporateMember ? '/corporate' : '/school');
  }, [navigate, refreshUser, isCorporateMember]);

  useEffect(() => {
    // Fetch school or corporate branding
    if (user?.school_id) {
      fetch(`${API_URL}/api/school/branding/${user.school_id}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setSchoolBranding(data); })
        .catch(() => {});
    } else if (user?.company_id) {
      fetch(`${API_URL}/api/corporate/branding/${user.company_id}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setSchoolBranding(data); })
        .catch(() => {});
    }
  }, [user?.school_id, user?.company_id]);

  // Auto-expand the group containing the active route
  useEffect(() => {
    for (const group of navGroups) {
      if (group.children?.some(c => location.pathname === c.path)) {
        setExpandedGroup(group.id);
        break;
      }
    }
  }, [location.pathname, navGroups]);

  const allNavGroups = ALL_NAV_GROUPS;
  // (allNavGroups kept as alias for any downstream JSX that may still reference it)
  void allNavGroups;

  const toggleGroup = (groupId) => {
    setExpandedGroup(prev => prev === groupId ? null : groupId);
  };

  const lastNavRef = useRef(null);
  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 768) onClose();
    // Watchdog: if the SPA router is wedged (stuck overlay, frozen tree after
    // device sleep), force a hard navigation so a menu tap can never be a no-op.
    lastNavRef.current = path;
    const target = path.split('?')[0];
    setTimeout(() => {
      if (lastNavRef.current === path && window.location.pathname !== target) {
        window.location.assign(path);
      }
    }, 450);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {isOpen && (
        <div
          className="mobile-overlay fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`sidebar ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <button
          onClick={onClose}
          className="md:hidden absolute top-4 right-3 text-white hover:text-orange-400 transition-colors z-50 min-w-[40px] min-h-[40px] flex items-center justify-center"
          aria-label="Close menu"
        >
          <X size={24} />
        </button>

        {/* Logo */}
        <div className="sidebar-logo">
          {isOrgUser ? (
            /* School/Corporate users: back arrow circle at top, org name below */
            <div className="flex flex-col items-center gap-3">
              {isPreviewMode && (
                <button
                  onClick={exitPreview}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 self-start"
                  style={{ backgroundColor: '#F5831F' }}
                  data-testid="back-to-admin-btn"
                  title="Back to Admin Dashboard"
                >
                  <ArrowLeft size={18} color="white" />
                </button>
              )}
              <span className="text-white font-bold text-base text-center" data-testid="sidebar-school-name-fallback">
                {schoolBranding?.school_name || schoolBranding?.org_name || 'Portal'}
              </span>
            </div>
          ) : schoolBranding ? (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center justify-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
                data-testid="sidebar-school-branding"
              >
                {schoolBranding.logo_url ? (
                  <img
                    src={`${API_URL}${schoolBranding.logo_url}`}
                    alt={schoolBranding.school_name}
                    style={{ height: 40, width: 'auto', maxWidth: 100, objectFit: 'contain' }}
                    data-testid="sidebar-school-logo"
                  />
                ) : (
                  <span className="text-white font-bold text-sm truncate" style={{ maxWidth: 100 }} data-testid="sidebar-school-name-fallback">
                    {schoolBranding.school_name}
                  </span>
                )}
                <div style={{ width: 1, height: 28, backgroundColor: schoolBranding.primary_color || '#666', flexShrink: 0 }} />
                <img src="/hibff-logo.png" alt="HiBFF" style={{ height: 28, width: 'auto', objectFit: 'contain' }} data-testid="sidebar-hibff-logo-secondary" />
              </button>
              {schoolBranding.tagline && (
                <p
                  className="text-xs mt-1.5 italic text-center px-2 leading-snug"
                  style={{ color: schoolBranding.primary_color || 'rgba(255,255,255,0.7)' }}
                  data-testid="sidebar-school-tagline"
                >
                  {schoolBranding.tagline}
                </p>
              )}
            </>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
            >
              <img src="/hibff-logo.png" alt="HiBFF" className="h-44 w-auto" />
            </button>
          )}
          {!schoolBranding && !isOrgUser && (
            <>
              <p className="text-sm text-white mt-2 font-semibold text-center tracking-wide" data-testid="sidebar-brand-line">Hi Brave Friends Forever</p>
              <p className="text-xs text-white/70 mt-1 italic text-center px-2 leading-snug" data-testid="sidebar-brand-tagline">
                Brave takes courage. Courage builds confidence. Confidence builds character.
              </p>
            </>
          )}
        </div>

        {/* Main Navigation */}
        <nav className="sidebar-nav flex-1 overflow-y-auto px-2">
          <ul className="space-y-1">
            {navGroups.map((item) => {
              if (item.type === 'link') {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`nav-item w-full ${isActive ? 'nav-item-active' : ''}`}
                      data-testid={`nav-${item.id}`}
                    >
                      <Icon size={20} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  </li>
                );
              }

              // Grouped item
              const Icon = item.icon;
              const isExpanded = expandedGroup === item.id;
              const hasActiveChild = item.children?.some(c => location.pathname === c.path);

              return (
                <li key={item.id}>
                  <button
                    onClick={() => toggleGroup(item.id)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-all ${
                      hasActiveChild
                        ? 'bg-white/15 text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`nav-group-${item.id}`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                    />
                  </button>

                  {/* Submenu */}
                  <ul
                    className={`overflow-hidden transition-all duration-200 ${
                      isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {item.children?.map((child, ci) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <li key={ci}>
                          <button
                            onClick={() => handleNavigation(child.path)}
                            className={`w-full text-left pl-12 pr-4 py-2.5 min-h-[40px] text-sm rounded-lg transition-all ${
                              isChildActive
                                ? 'text-white bg-white/15 font-medium'
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                            }`}
                            data-testid={`nav-${child.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                          >
                            {child.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>

          {/* Divider */}
          <div className="my-4 mx-2 border-t border-white/10" />

          {/* Profile Completion (compact) */}
          <ProfileCompletionBar compact />

          {/* Bottom links */}
          <ul className="space-y-1">
            {bottomLinks.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={index}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`nav-item w-full ${isActive ? 'nav-item-active' : ''} py-2 text-sm ${item.highlight ? 'opacity-100' : 'opacity-80'}`}
                    style={item.highlight ? { background: 'linear-gradient(90deg, rgba(245,131,31,0.15), transparent)', borderLeft: '3px solid #F5831F' } : {}}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon size={18} style={item.highlight ? { color: '#F5831F' } : {}} />
                    <span style={item.highlight ? { fontWeight: 600 } : {}}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Sprint 208 Master item #5 — HiBFF Privacy Shield panel
              (parent-only). Explicitly reinforces why text logs are locked. */}
          {isParent && (
            <div
              className="mt-4 p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200/70 text-[11px] leading-snug"
              data-testid="parent-sidebar-privacy-shield"
            >
              <p className="font-bold text-emerald-800 mb-1.5 flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                HiBFF Privacy Shield
              </p>
              <ul className="space-y-1 text-gray-700">
                <li className="flex items-start gap-1.5"><span className="text-emerald-600 mt-0.5">●</span><span><strong>PII Redaction</strong> — your teen&apos;s personal details never leave the device</span></li>
                <li className="flex items-start gap-1.5"><span className="text-emerald-600 mt-0.5">●</span><span><strong>Conversation Privacy</strong> — chat logs locked from this dashboard</span></li>
                <li className="flex items-start gap-1.5"><span className="text-emerald-600 mt-0.5">●</span><span><strong>Auto-Delete</strong> — sessions cleared on a strict rolling window</span></li>
                <li className="flex items-start gap-1.5"><span className="text-emerald-600 mt-0.5">●</span><span><strong>Safety Guardrails</strong> — 9 domains actively filtered in real time</span></li>
              </ul>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer relative">
          {showProfileDropdown && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => { setShowProfileDropdown(false); navigate('/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <UsersIcon size={18} className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700">View Profile</span>
              </button>
              <button
                onClick={() => { setShowProfileDropdown(false); navigate('/settings'); }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <Settings size={18} className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Account Settings</span>
              </button>
              <div className="border-t border-gray-200" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left"
              >
                <LogOut size={18} className="text-red-600" />
                <span className="text-sm font-medium text-red-600">Logout</span>
              </button>
            </div>
          )}

          <button
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="w-full flex items-center space-x-3 p-4 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors cursor-pointer"
          >
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-orange-400 flex items-center justify-center text-white font-bold text-lg">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-white/70 truncate">
                {user?.subscription_tier_name === 'Free Tier' ? 'ENTRY' : (user?.subscription_tier_name || 'ENTRY')}
              </p>
            </div>
            <ChevronUp
              size={18}
              className={`text-white/70 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
